import clsx from "clsx";
import { Button, Label, QQSection, RadioButton, ToolTip } from "common/components";
import React, { useState } from "react";
import { SubmitHandler, useForm, UseFormRegister } from "react-hook-form";
import { CalendarInput, ShipToTypeAhead } from ".";
import styles from "./ShipToDateForm.module.scss";
import { FormFields, transportOptions } from "../utils";
import { trackGenerateQuote } from "common/tagManagerService";
import { SoldToTypeAhead } from "./SoldToTypeAhead";
import { appState, useAppSelector } from "app";
import { useEgerdauToken } from "common/services";

interface ShipDateFormProps {
  fileName: string;
  cityIdInit?: string;
  requestDateInit: string;
  shipToInit?: string;
  transportationInit?: string;
  soldToInit?: string;
  soldToIdInit?: string;
  onSubmit?: (values: FormFields) => void;
}

const transportRadios = (register: UseFormRegister<FormFields>) => {
  const { ref, ...restProps } = register("transportation");
  return (
    <ul className="d-flex mt-7">
      {transportOptions.map((option) => (
        <li key={option.id} className="form-check form-check-inline">
          <RadioButton id={option.id} value={option.value} ref={ref} {...restProps} />
          <Label htmlFor={option.id}>{option.label}</Label>
        </li>
      ))}
    </ul>
  );
};

export const ShipToDateForm: React.FunctionComponent<ShipDateFormProps> = ({
  fileName,
  cityIdInit,
  requestDateInit,
  shipToInit,
  transportationInit,
  soldToInit,
  soldToIdInit,
  onSubmit = () => {},
}) => {
  const { customer } = useAppSelector(appState);
  const token = useEgerdauToken();
  const gerdauEmailDomain = "@gerdau.com";
  const isGerdauUser = customer?.uid?.includes(gerdauEmailDomain) || false;

  const [cityId, setCityId] = useState<string>(cityIdInit ?? "");
  const [soldToId, setSoldToId] = useState<string>(soldToIdInit ?? "");
  const [soldToName, setSoldToName] = useState<string>("");

  const { register, setValue, watch, handleSubmit } = useForm<FormFields>({
    defaultValues: {
      transportation: transportationInit ?? "01",
      requestDate: requestDateInit,
      shipTo: shipToInit ?? "",
      soldTo: soldToInit ?? "",
      soldToId: soldToIdInit ?? "",
      soldToName: "",
    },
    mode: "onBlur",
  });
  const { shipTo, requestDate, soldTo } = watch();

  const canContinue = () => {
    const hasShipTo = shipTo.length > 0;
    const hasSoldTo = isGerdauUser ? soldTo && soldTo.length > 0 : true;
    return hasShipTo && hasSoldTo;
  };

  const onSubmitHandler: SubmitHandler<FormFields> = (data) => {
    onSubmit({
      ...data,
      cityId,
      soldToId: data.soldToId || soldToId,
      soldToName: data.soldToName || soldToName,
    });
    trackGenerateQuote(data);
  };

  const handleSoldToChange = (id: string, name: string, _displayValue: string) => {
    setSoldToId(id);
    setSoldToName(name);
  };

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)}>
      {isGerdauUser && (
        <QQSection blue>
          <div className={clsx(styles.container, "mt-30 mx-30 pb-30")}>
            <div className={clsx("d-flex align-items-center mb-30", styles.heading)}>
              <div className="flex-grow-1">
                <h5 className="font-size-20 text-dark fw-bold m-0">Sold To</h5>
              </div>
            </div>
            <div className={styles.soldToSection}>
              <SoldToTypeAhead
                register={register}
                setValue={setValue}
                defaultValue={soldToInit}
                token={token}
                onSoldToChange={handleSoldToChange}
              />
            </div>
          </div>
        </QQSection>
      )}
      <QQSection blue>
        <div className={clsx(styles.container, "mt-30 mx-30 pb-30")}>
          <div className={clsx("d-flex align-items-center mb-30", styles.heading)}>
            <div className="flex-grow-1">
              <h5 className="font-size-20 text-dark fw-bold m-0">Shipping & Requested Date</h5>
            </div>
            <div className={clsx("font-size-16", styles.fileName)}>File: {fileName}</div>
          </div>
          <div className={clsx("d-flex", styles.formFields)}>
            <div className={styles.shippingMethod}>
              <Label>Shipping Method</Label>
              {transportRadios(register)}
            </div>
            <div className={styles.shipTo}>
              <ShipToTypeAhead
                labelText="Ship To"
                register={register}
                setValue={setValue}
                defaultShipToValue={shipTo}
                onCityIdChange={(value) => setCityId(value)}
              />
            </div>
            <div className={styles.requestedDeliveryDate}>
              <CalendarInput
                labelText="Requested Delivery Date"
                register={register}
                setValue={setValue}
                requestDate={requestDate}
              />
            </div>
            <div className={styles.generateQuoteButton}>
              <ToolTip
                placement="top"
                popOverText={
                  isGerdauUser
                    ? "Please enter Sold To, Ship To City and State"
                    : "Please enter Ship To City and State"
                }
                enableTip={!canContinue()}
              >
                <span className="d-inline-block">
                  <Button
                    disabled={!canContinue()}
                    className={clsx({ [styles.pointerEvents]: !canContinue() })}
                    type="submit"
                  >
                    Generate Quote
                  </Button>
                </span>
              </ToolTip>
            </div>
          </div>
        </div>
      </QQSection>
    </form>
  );
};
